/* eslint-disable react/prop-types */
import {lazy, Suspense, useCallback, useContext, useEffect, useRef} from 'react'

import cx from 'classnames'
import PropTypes from 'prop-types'

import {useUserConsents} from '@s-ui/consents'
import jsCookie from '@s-ui/js/lib/cookie'
import {useDecision} from '@s-ui/pde'
import Context from '@s-ui/react-context'
import Helmet from '@s-ui/react-head'
import {useMount} from '@s-ui/react-hooks'
import {useLocation} from '@s-ui/react-router'
import ScriptJsonLD from '@s-ui/react-script-json-ld'
import AdDisplay from '@adv-mt/mt-ad-display'
import EmptyCase from '@adv-mt/mt-empty-case-default'
import Layout, {layoutBasicBackgroundColors} from '@adv-mt/mt-layout-basic'
import {useMediaQuery, useMediaQueryBreakpoints} from '@adv-mt/mt-layout-render-media-query'
import ListAds from '@adv-mt/mt-list-ads'
import ListPagination, {pageNumberPlaceholder} from '@adv-mt/mt-list-pagination'
import MotorTagCloud from '@adv-mt/mt-list-tagcloud'
import Breadcrumb from '@adv-mt/mt-navigation-breadcrumb'
import NotificationsAdFavorite from '@adv-mt/mt-notifications-ad-favorite'
import NotificationsSortManagement from '@adv-mt/mt-notifications-sort-management'
import PanelBasic from '@adv-mt/mt-panel-basic'
import PanelFaqs from '@adv-mt/mt-panel-faqs'
import SearchAiTopBar, {searchAiTopBarPlaces} from '@adv-mt/mt-search-ai-top-bar'
import SearchSave, {searchSaveTrackingOrigins} from '@adv-mt/mt-search-save'
import SearchSidebar, {searchSidebarDesigns, searchSidebarPlaces} from '@adv-mt/mt-search-sidebar'
import SearchSort, {searchSortButtonDesigns, searchSortPlaces} from '@adv-mt/mt-search-sort'
import {useSearchTags} from '@adv-mt/mt-search-tags'
import {sharedTopbarMaxContentWidths} from '@adv-mt/mt-shared-topbar'
import TitleBasic, {titleBasicSizes, titleBasicTags, titleBasicTextAlign} from '@adv-mt/mt-title-basic'
import {useAnalyticsTrack} from '@adv-mt/mt-tracking-use-analytics'
import {useCsat} from '@adv-mt/mt-tracking-use-csat'

import LinkFactory from '../../components/LinkFactory/index.js'
import CsatNpsTracker from '../../helpers/csatNps/CsatNpsTracker.js'
import ComponentRecommender from '../AdsList/components/RecommenderCarousel.js'
import SearchTagsBar from '../AdsList/experiments/searchTagsBar/index.js'
import NotificationAdNotAvailable from '../AdsList/notificationAdNotAvailable.js'
import {createPaginationUrlFactory, PAGE_ONE, scrollIntoViewVisitedAd} from '../AdsList/utils.js'
import {getInitialProps, NOTIFICATIONS, PAGINATION_PLACEHOLDER} from './getInitialProps.js'
import Skeleton from './Skeleton.js'

const {FullWidth: LayoutFullWidth} = Layout

const NotificationNoAlertMatches = lazy(() =>
  import(/* webpackChunkName: "NotificationNoAlertMatches" */ '../AdsList/notificationNoAlertMatches.js')
)

const DEFAULT_TOTAL_PAGES = 1

const SubscriptionEmptyCase = () => {
  const emptyCaseProps = {
    imageSize: 'medium',
    imageUrl: 'images/empty-cases/empty-case-search.svg',
    text: 'EMPTY_CASE_NO_RESULTS_NO_ALERTS_TEXT',
    title: 'EMPTY_CASE_NO_RESULTS_TITLE'
  }
  return <EmptyCase {...emptyCaseProps} />
}

const AdsListSubscription = ({
  __HTTP__ = {},
  breadcrumb,
  faqsForUrlItems,
  h1 = '',
  hasNoMatchesUserAlertInformation,
  helmet = {},
  initialSearch = {},
  isLoading,
  lastFilter,
  listFiltersOptions,
  listingSectionId,
  loadThirdParties,
  notifications = {},
  pageErrors = {},
  paginationPlaceholderUrl,
  pageName,
  isRecommendedAdsFallback: isRecommendedAdsFallbackValue = false,
  recommendedAds = {},
  router,
  searchResults = {},
  showSortManagementNotification,
  structuredDataJsonList = [],
  pageFaqs = {},
  tagclouds,
  trackingData,
  createdAlert,
  visitedAdData = {}
}) => {
  // Even though this is placed at the top of the page,
  // When we do Client-side rendering the component is rendered
  const {redirectTo} = __HTTP__

  const {pathname, search} = useLocation()
  const searchTopbarOverlayContentRef = useRef()
  const searchSidebarOverlayContentRef = useRef()

  const {page: currentPage} = initialSearch || {}
  /**
   * Hack for now until we tackle this ticket MTR-103811
   * Without this, price filter breaks
   */
  initialSearch.monthlySubscriptionFee = initialSearch.fee

  const {trigger} = useCsat()

  useEffect(() => {
    domain.get('save_navigation_data_ads_use_case').execute({
      ads: searchResults.items,
      currentPage,
      placeholder: PAGINATION_PLACEHOLDER,
      placeholderUrl: paginationPlaceholderUrl,
      searchUrl: `${pathname}${search}`,
      totalPages: searchResults.totalPages,
      totalResults: searchResults.totalResults
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchResults, currentPage])

  useEffect(() => {
    trigger('csat_renting')
  }, [initialSearch]) // eslint-disable-line

  const {domain, i18n} = useContext(Context)

  const config = domain.get('config')

  const {LIST_VIEW_EVENT} = config.get('tracking')

  const {
    CONSENTS: {LAST_ADS_SEARCH: lastAdsSearchRequiredConsents},
    COOKIES: {IS_VIEWED_SORT_MANAGEMENT_NOTIFICATION, VISITED_AD: visitedAdCookieKey}
  } = config.get('COMMON')

  const showAdNotAvailableNotification = Boolean(notifications[NOTIFICATIONS.AD_NOT_AVAILABLE])

  const isMobile = config.get('isMobile')
  const isTablet = config.get('isTablet')

  const isDesktopOrWider = useMediaQuery(useMediaQueryBreakpoints.lg, {
    defaultMatches: !isMobile && !isTablet
  })

  const {enabled: isTallAdThreeCards} = useDecision('ff_web_buyer_ads_grid_ad_3_cards')

  const hasConsentsForLastAdsSearch = useUserConsents(lastAdsSearchRequiredConsents)

  const {track} = useAnalyticsTrack()

  const {searchResultsError} = pageErrors
  const {items: subscriptionAds} = searchResults

  const {totalResults = 0} = searchResults || {}

  const hasResults = Number(totalResults) > 0

  const hasRecommendedAds = Boolean(recommendedAds?.items?.length)

  const searchTags = useSearchTags({pageName, search: initialSearch})
  const hasTags = Boolean(searchTags.length)

  const {title: faqsTitle, faqs = {}, seoFaqs = {}} = pageFaqs || {}
  const hasFaqs = Boolean(faqs?.items?.length)
  const stringifiedSeoFaqs = hasFaqs ? JSON.stringify(seoFaqs) : ''
  const showSpecificFaqsForUrl = Boolean(faqsForUrlItems && faqsForUrlItems.length)

  const getLastSearchTitle = ({searchTags}) => {
    const tagsText = searchTags.map(({label}) => label).join(', ')
    const title = i18n.t('SEARCH.SUBSCRIPTION')
    return `${title} ${tagsText}`
  }

  const handleShowSortManagementNotification = useCallback(
    () => jsCookie.set(IS_VIEWED_SORT_MANAGEMENT_NOTIFICATION, true),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  )

  useEffect(() => {
    const query = router.location.query
    const {categoryId, filteredOfferTypeId, offerTypeId} = initialSearch
    const params = {categoryId, filteredOfferTypeId, offerTypeId}

    const hasFilters = Boolean(Object.keys(query).length)
    const hasLastFilter = Boolean(lastFilter)

    const shouldSaveSearch = totalResults && hasFilters && hasLastFilter && hasConsentsForLastAdsSearch
    const title = hasTags
      ? getLastSearchTitle({
          searchTags
        })
      : h1

    shouldSaveSearch && domain.get('save_last_ads_search_use_case').execute({params, pageName, query, title})
  }, [initialSearch, hasConsentsForLastAdsSearch]) // eslint-disable-line

  useMount(() => {
    visitedAdData.isAccessible && scrollIntoViewVisitedAd({adId: visitedAdData.id, cookieKey: visitedAdCookieKey})
    if (isRecommendedAdsFallbackValue) {
      track({
        event: 'Recommended Ads Fallback',
        data: {
          hit_origin: pageName,
          hit_information: 'true'
        }
      })
    }
  })

  useMount(() => {
    const {unsubscribe} = domain.get('get_url_params_search_use_case').subscribe(({params, result}) => {
      const [, urlParams] = result
      router.push({
        pathname: urlParams.path,
        search: urlParams.query,
        state: {
          lastFilter: params.lastFilter
        }
      })
    })
    return unsubscribe
  })

  // List Viewed tagging should occur every time there's a new search
  useEffect(() => {
    const getTrackingData = async () =>
      domain.get('get_page_tracking_data_use_case').execute({
        advertisements: subscriptionAds,
        lastFilter,
        page: pageName,
        pageNumber: currentPage,
        search: initialSearch,
        searchResultsCount: totalResults
      })

    getTrackingData().then(({data}) => {
      track({event: LIST_VIEW_EVENT, data})
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialSearch])

  const {aggregations, totalPages = DEFAULT_TOTAL_PAGES} = searchResults || {}

  const hasPagination = totalPages > PAGE_ONE
  const handleRedirectTo = url => {
    router.push(url)
  }

  if (redirectTo) {
    handleRedirectTo(redirectTo)
    return <></>
  }

  const adsListClassNames = cx('mt-AdsListSubscription', 'mt-AdsListSubscription--grid')

  return (
    <LayoutFullWidth
      linkFactory={LinkFactory}
      isMarginless
      isPaddingless
      bgColor={layoutBasicBackgroundColors.INHERIT}
      topbarProps={{
        maxContentWidth: sharedTopbarMaxContentWidths.EXTRA_LARGE
      }}
    >
      <CsatNpsTracker />
      <div className="mt-AdsListSubscription-searchTopbar">
        {!isDesktopOrWider && (
          <SearchAiTopBar
            filtersProps={listFiltersOptions}
            isLoading={isLoading}
            listingSectionId={listingSectionId}
            overlayContentRef={searchTopbarOverlayContentRef}
            pageName={pageName}
            place={searchAiTopBarPlaces.ADS_LIST_SUBSCRIPTION}
            search={initialSearch}
            totalResults={totalResults}
            withAiQuickFilters={true}
          />
        )}
      </div>
      {!isDesktopOrWider && hasTags && (
        <div className="mt-AdsListSubscription-searchSave">
          <SearchSave
            createdAlert={createdAlert}
            isFullWidth={false}
            search={initialSearch}
            trackingOrigin={searchSaveTrackingOrigins.ADS_LIST}
            showTooltip={false}
          />
        </div>
      )}
      <main className={adsListClassNames} ref={searchTopbarOverlayContentRef}>
        <div className="mt-AdsListSubscription-titleHeader">
          <div className="mt-AdsListSubscription-title">
            <TitleBasic
              as={titleBasicTags.h1}
              description={i18n.t('COMMON.SUBSCRIPTION_MONTHLY_FEE_ADVICE')}
              descriptionTextAlign={isDesktopOrWider ? titleBasicTextAlign.left : titleBasicTextAlign.center}
              size={titleBasicSizes.xxs}
              textAlign={isDesktopOrWider ? titleBasicTextAlign.left : titleBasicTextAlign.center}
              title={h1}
            />
          </div>
          {isDesktopOrWider ? (
            <SearchSort
              design={searchSortButtonDesigns.BASIC}
              linkFactory={LinkFactory}
              listingSectionId={listingSectionId}
              pageName={pageName}
              place={searchSortPlaces.ADS_LIST_SUBSCRIPTION}
              search={initialSearch}
              withSortOrderInfo
            />
          ) : (
            showSortManagementNotification && (
              <NotificationsSortManagement onClose={handleShowSortManagementNotification} />
            )
          )}
        </div>
        {isDesktopOrWider && (
          <SearchTagsBar
            pageName={pageName}
            createdAlert={createdAlert}
            listingSectionId={listingSectionId}
            search={initialSearch}
            place={searchSidebarPlaces.SUBSCRIPTION_ADS_LIST}
            searchSaveTrackingOrigin={searchSaveTrackingOrigins.ADS_LIST}
          />
        )}
        <div className="mt-AdsListSubscription-inner">
          {isDesktopOrWider && (
            <div className="mt-AdsListSubscription-sidebar">
              <SearchSidebar
                aggregations={aggregations}
                design={searchSidebarDesigns.WRAPPED}
                filtersProps={listFiltersOptions}
                listingSectionId={listingSectionId}
                overlayContentRef={searchSidebarOverlayContentRef}
                pageName={pageName}
                place={searchSidebarPlaces.SUBSCRIPTION_ADS_LIST}
                redirectTo={handleRedirectTo}
                search={initialSearch}
                showSearchSaveButton={false}
                withLastSavedSearches
                withAiQuickFilters={true}
                isFullWidth
              />
            </div>
          )}
          <section className="mt-AdsListSubscription-content" ref={searchSidebarOverlayContentRef}>
            {searchResultsError ? (
              i18n.t('ERROR_MESSAGE_SERVER_ERROR_COCHES_TEXT')
            ) : (
              <>
                {hasNoMatchesUserAlertInformation && (
                  <Suspense fallback={<></>}>
                    <NotificationNoAlertMatches />
                  </Suspense>
                )}
                {showAdNotAvailableNotification && (
                  <div className="mt-AdsList-notifications">
                    <NotificationAdNotAvailable />
                  </div>
                )}
                <ListAds
                  ads={subscriptionAds}
                  adSpaces={
                    hasRecommendedAds
                      ? [
                          {
                            position: visitedAdData.position + 1,
                            // A render prop so the carousel can match its visible slides to the
                            // columns the grid layout renders.
                            renderComponent: ({gridColumns}) => (
                              <ComponentRecommender
                                extendedTrackingData={trackingData?.value?.data}
                                gridColumns={gridColumns}
                                recommendedAds={recommendedAds.items}
                              />
                            )
                          }
                        ]
                      : undefined
                  }
                  count={searchResults.items?.length}
                  currentPage={currentPage}
                  linkFactory={LinkFactory}
                  extendedTrackingData={trackingData?.value?.data}
                  pageName={pageName}
                  withAdViewedTracking
                  isGridLayout
                />
                {hasPagination ? (
                  <div className="mt-AdsListSubscription-pagination">
                    <ListPagination
                      currentPage={currentPage}
                      totalPages={totalPages}
                      linkFactory={LinkFactory}
                      urlPattern={paginationPlaceholderUrl}
                      createUrl={createPaginationUrlFactory(pageNumberPlaceholder)}
                    />
                  </div>
                ) : null}
              </>
            )}
            {!hasResults ? <SubscriptionEmptyCase /> : null}
          </section>
        </div>
        {showSpecificFaqsForUrl && (
          <section className="mt-AdsListSubscription-faqsForUrl">
            <TitleBasic as={titleBasicTags.h3} title={i18n.t('COMMON.FAQ_TITLE')} size={titleBasicSizes.s} />
            <PanelFaqs questions={faqsForUrlItems} />
          </section>
        )}
        {hasFaqs && (
          <section className="mt-AdsListSubscription-section">
            <div className="mt-AdsListSubscription-sectionTitle">
              <TitleBasic as={titleBasicTags.h3} title={i18n.t(faqsTitle)} size={titleBasicSizes.xs} />
            </div>
            <PanelBasic>
              <PanelFaqs questions={faqs?.items} faqsStructuredData={stringifiedSeoFaqs} />
            </PanelBasic>
          </section>
        )}
        <div className="mt-AdsListSubscription-tagcloudsWrapper">
          <TitleBasic as={titleBasicTags.h3} title={i18n.t('COMMON.RELATED_SEARCH')} size={titleBasicSizes.s} />
          <MotorTagCloud initialTagclouds={tagclouds} linkFactory={LinkFactory} pageName={pageName} />
        </div>

        <Breadcrumb items={breadcrumb} linkFactory={LinkFactory} />
      </main>
      <NotificationsAdFavorite />
      <Helmet {...helmet} />
      {structuredDataJsonList.map((structuredData, index) => (
        <ScriptJsonLD json={structuredData} key={index} />
      ))}
      {loadThirdParties && (
        <AdDisplay
          pageName={pageName}
          search={initialSearch}
          experimentFlags={[`ff_web_buyer_ads_grid_ad_3_cards-${isTallAdThreeCards ? 'on' : 'off'}`]}
        />
      )}
    </LayoutFullWidth>
  )
}

AdsListSubscription.displayName = 'AdsListSubscription'

AdsListSubscription.propTypes = {
  __HTTP__: PropTypes.object,
  breadcrumb: PropTypes.array,
  currentPage: PropTypes.number,
  h1: PropTypes.string,
  hasNoMatchesUserAlertInformation: PropTypes.bool,
  helmet: PropTypes.object,
  initialSearch: PropTypes.object,
  lastFilter: PropTypes.string,
  listFiltersOptions: PropTypes.object,
  listingSectionId: PropTypes.string,
  loadThirdParties: PropTypes.bool,
  makeSlug: PropTypes.string,
  notifications: PropTypes.object,
  pageErrors: PropTypes.object,
  paginationPlaceholderUrl: PropTypes.string,
  pageName: PropTypes.string,
  router: PropTypes.object,
  searchResults: PropTypes.object,
  pageFaqs: PropTypes.object,
  structuredDataJsonList: PropTypes.array,
  subscriptionMakes: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      name: PropTypes.string,
      slug: PropTypes.string
    })
  ),
  subscriptionModels: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      name: PropTypes.string,
      slug: PropTypes.string
    })
  ),
  tagclouds: PropTypes.array,
  trackingData: PropTypes.object,
  createdAlert: PropTypes.object
}

// this element helps avoid poor CLS performance on Detail page when on SPA
AdsListSubscription.renderLoading = () => <Skeleton />

AdsListSubscription.getInitialProps = getInitialProps
// It keeps page mounted in order to avoid flickering when user filters
AdsListSubscription.keepMounted = true

export default AdsListSubscription
